import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import TableThumbnail from '../components/TableThumbnail';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import { isAmxAdmin } from '../utils/auth';
import { canManageBusinessUnit } from '../utils/businessUnitAccess';
import {
  ACCEPTANCE_MODES,
  CAPABILITY_STATUSES,
  CAPABILITY_TYPES,
  CAPABILITY_VISIBILITIES,
  FULFILLMENT_MODES,
  formatCapabilityLabel,
} from '../utils/capabilityContract';
import {
  getAccountPartnerMembership,
  getMembershipDisplayName,
} from '../utils/userProfile';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

function optionLabel(options, value) {
  if (!value) {
    return 'N/A';
  }
  return options.find((option) => option.value === value)?.label || formatCapabilityLabel(value);
}

function normalize(value) {
  return String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
}

export default function CapabilitiesDatabase(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const partnerProfile = useSelector((state) => state.auth.partnerProfile);
  const admin = isAmxAdmin(user);
  const businessUnitFilter = new URLSearchParams(location.search).get('business_unit_id') || '';
  const [capabilityData, setCapabilityData] = useState({ filters: {}, capabilities: [] });
  const [businessUnitData, setBusinessUnitData] = useState({ business_units: [] });
  const [filters, setFilters] = useState({
    business_unit_id: businessUnitFilter,
    capability_type: '',
    status: '',
    fulfillment_mode: '',
    search: '',
  });
  const [capabilityFeedback, setCapabilityFeedback] = useState(null);
  const [deletingCapabilityId, setDeletingCapabilityId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const activeMembership = useMemo(
    () => getAccountPartnerMembership(partnerProfile),
    [partnerProfile],
  );
  const useAdminScope = admin && !activeMembership?.partner_id;
  const capabilityContextLabel = activeMembership
    ? getMembershipDisplayName(activeMembership)
    : useAdminScope
      ? 'AMX admin scope'
      : 'your associated partner or business unit';
  const capabilityEndpoint = useAdminScope ? '/capabilities' : '/partners/user/capabilities';

  const updateFilters = useCallback((patch) => {
    setFilters((current) => ({ ...current, ...patch }));
  }, []);

  const filteredCapabilities = useMemo(() => {
    const query = normalize(filters.search);
    return capabilityData.capabilities.filter((capability) => {
      if (filters.business_unit_id && capability.business_unit_id !== filters.business_unit_id) {
        return false;
      }
      if (
        filters.capability_type
        && (capability.capability_type || capability.type) !== filters.capability_type
      ) {
        return false;
      }
      if (filters.status && capability.status !== filters.status) {
        return false;
      }
      if (filters.fulfillment_mode && capability.fulfillment_mode !== filters.fulfillment_mode) {
        return false;
      }
      if (!query) {
        return true;
      }
      return normalize([
        capability.capability_name,
        capability.source_capability_definition_id,
        capability.business_unit_display_name,
        capability.business_unit_code,
        capability.partner_display_name,
        capability.partner_code,
        capability.category_display_name,
        capability.category_name,
        capability.capability_type || capability.type,
        capability.status,
        capability.visibility,
        capability.fulfillment_mode,
        capability.acceptance_mode,
      ].filter(Boolean).join(' ')).includes(query);
    });
  }, [capabilityData.capabilities, filters]);

  const totalAssets = useMemo(
    () => filteredCapabilities.reduce(
      (total, capability) => total + (capability.assets?.length || 0),
      0,
    ),
    [filteredCapabilities],
  );

  const manageableBusinessUnits = useMemo(() => {
    const units = Array.isArray(businessUnitData.business_units) ? businessUnitData.business_units : [];
    return units.filter((unit) => canManageBusinessUnit(
      partnerProfile,
      unit.business_unit_id,
      unit.parent_partner_id,
    ));
  }, [businessUnitData.business_units, partnerProfile]);

  const filterBusinessUnits = useMemo(() => {
    const units = new Map();
    const availableUnits = Array.isArray(businessUnitData.business_units)
      ? businessUnitData.business_units
      : [];
    availableUnits.forEach((unit) => {
      units.set(unit.business_unit_id, unit);
    });
    capabilityData.capabilities.forEach((capability) => {
      if (!units.has(capability.business_unit_id)) {
        units.set(capability.business_unit_id, {
          business_unit_id: capability.business_unit_id,
          display_name: capability.business_unit_display_name,
          business_unit_code: capability.business_unit_code,
          parent_partner: {
            display_name: capability.partner_display_name,
            partner_code: capability.partner_code,
          },
        });
      }
    });
    return Array.from(units.values()).filter((unit) => unit.business_unit_id);
  }, [businessUnitData.business_units, capabilityData.capabilities]);

  const selectedFilterBusinessUnit = useMemo(
    () => filterBusinessUnits.find((unit) => unit.business_unit_id === filters.business_unit_id),
    [filterBusinessUnits, filters.business_unit_id],
  );

  const canCreateCapability = Boolean(activeMembership?.partner_id && manageableBusinessUnits.length);

  const fetchCapabilities = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    setError('');
    try {
      const response = await axiosInstance.get(capabilityEndpoint, {
        params: filters.business_unit_id ? { business_unit_id: filters.business_unit_id } : {},
      });
      const apiData = response?.data ?? {};
      setCapabilityData({
        filters: apiData.filters ?? {},
        capabilities: Array.isArray(apiData.capabilities) ? apiData.capabilities : [],
      });
    } catch (err) {
      setCapabilityData({ filters: {}, capabilities: [] });
      setError(err?.response?.data?.message || err?.message || 'Failed to fetch your capabilities.');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [capabilityEndpoint, filters.business_unit_id]);

  const fetchBusinessUnits = useCallback(async () => {
    if (useAdminScope || !activeMembership?.partner_id) {
      setBusinessUnitData({ business_units: [] });
      return;
    }
    try {
      const response = await axiosInstance.get('/partners/user/business-units');
      setBusinessUnitData(response?.data || { business_units: [] });
    } catch (_error) {
      setBusinessUnitData({ business_units: [] });
    }
  }, [activeMembership?.partner_id, useAdminScope]);

  useEffect(() => {
    fetchCapabilities();
  }, [fetchCapabilities]);

  useEffect(() => {
    updateFilters({ business_unit_id: businessUnitFilter });
  }, [businessUnitFilter, updateFilters]);

  useEffect(() => {
    fetchBusinessUnits();
  }, [fetchBusinessUnits]);

  const openCreateCapability = useCallback(() => {
    if (!canCreateCapability) {
      setCapabilityFeedback({
        title: 'Business Unit Access Required',
        message: 'Create a Business Unit or ask for access to one before adding capabilities.',
      });
      return;
    }
    navigate('/capabilities/new');
  }, [canCreateCapability, navigate]);

  const openCapability = useCallback((capability) => {
    navigate(`/capabilities/${encodeURIComponent(capability.capability_definition_id)}`);
  }, [navigate]);

  const associateAsset = useCallback((capability) => {
    navigate(`/capabilities/${encodeURIComponent(capability.capability_definition_id)}`, {
      state: { initialTab: 'assets' },
    });
  }, [navigate]);

  const handleDeleteCapability = useCallback(async (capability) => {
    const confirmed = window.confirm(
      `Delete capability "${capability.capability_name || 'Unnamed Capability'}"? Supporting asset records will remain registered.`,
    );
    if (!confirmed) {
      return;
    }

    setError('');
    setCapabilityFeedback(null);
    setDeletingCapabilityId(capability.capability_definition_id);
    try {
      await axiosInstance.delete(
        `/partners/${encodeURIComponent(capability.partner_id)}/capabilities/${encodeURIComponent(capability.capability_definition_id)}`,
      );
      setCapabilityFeedback({
        title: 'Capability Deleted',
        message: `${capability.capability_name || 'The capability'} was removed.`,
      });
      await fetchCapabilities({ silent: true });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to delete capability.');
    } finally {
      setDeletingCapabilityId('');
    }
  }, [fetchCapabilities]);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />

        <Box component="main" sx={{ flexGrow: 1, p: 3, minWidth: 0 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Capabilities' },
            ]}
          />

          <Stack spacing={3}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2, md: 3 },
                bgcolor: 'background.default',
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <PrecisionManufacturingRoundedIcon color="primary" />
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                      Capabilities
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Capability inventory visible to {capabilityContextLabel}.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <Chip label={`${filteredCapabilities.length} capabilities`} />
                  <Chip label={`${totalAssets} assets`} />
                  <Button
                    variant="contained"
                    startIcon={<AddCircleRoundedIcon />}
                    onClick={openCreateCapability}
                    disabled={!canCreateCapability}
                  >
                    Add Capability
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
                <TextField
                  value={filters.search}
                  onChange={(event) => updateFilters({ search: event.target.value })}
                  placeholder="Search Capabilities..."
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: { xs: '100%', lg: 220 } }}>
                  <InputLabel id="capabilities-business-unit-filter-label">Business Unit</InputLabel>
                  <Select
                    labelId="capabilities-business-unit-filter-label"
                    label="Business Unit"
                    value={filters.business_unit_id}
                    onChange={(event) => updateFilters({ business_unit_id: event.target.value })}
                  >
                    <MenuItem value="">All Business Units</MenuItem>
                    {filterBusinessUnits.map((unit) => (
                      <MenuItem key={unit.business_unit_id} value={unit.business_unit_id}>
                        {unit.display_name || unit.business_unit_code || unit.business_unit_id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', lg: 160 } }}>
                  <InputLabel id="capabilities-type-filter-label">Type</InputLabel>
                  <Select
                    labelId="capabilities-type-filter-label"
                    label="Type"
                    value={filters.capability_type}
                    onChange={(event) => updateFilters({ capability_type: event.target.value })}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {CAPABILITY_TYPES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', lg: 160 } }}>
                  <InputLabel id="capabilities-status-filter-label">Status</InputLabel>
                  <Select
                    labelId="capabilities-status-filter-label"
                    label="Status"
                    value={filters.status}
                    onChange={(event) => updateFilters({ status: event.target.value })}
                  >
                    <MenuItem value="">Any Status</MenuItem>
                    {CAPABILITY_STATUSES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', lg: 180 } }}>
                  <InputLabel id="capabilities-fulfillment-filter-label">Fulfillment</InputLabel>
                  <Select
                    labelId="capabilities-fulfillment-filter-label"
                    label="Fulfillment"
                    value={filters.fulfillment_mode}
                    onChange={(event) => updateFilters({ fulfillment_mode: event.target.value })}
                  >
                    <MenuItem value="">Any Fulfillment</MenuItem>
                    {FULFILLMENT_MODES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              {filters.business_unit_id ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                  {selectedFilterBusinessUnit?.parent_partner?.display_name
                    || selectedFilterBusinessUnit?.parent_partner?.partner_code
                    || ''}
                </Typography>
              ) : null}
            </Paper>

            {loading ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={24} />
                <Typography>Loading capabilities...</Typography>
              </Stack>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : capabilityData.capabilities.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Add your first capability
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    A capability describes work your partner can perform for requesters.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddCircleRoundedIcon />}
                    onClick={openCreateCapability}
                    disabled={!canCreateCapability}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Add Capability
                  </Button>
                </Stack>
              </Paper>
            ) : filteredCapabilities.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No capabilities match the current search and filters.
                </Typography>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 1320 }} aria-label="Capabilities">
                  <TableHead>
                    <TableRow>
                      <TableCell>Capability</TableCell>
                      <TableCell>Business Unit</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Fulfillment Mode</TableCell>
                      <TableCell>Acceptance Mode</TableCell>
                      <TableCell>Visibility</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Assets</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCapabilities.map((capability) => {
                      const assetCount = Array.isArray(capability.assets) ? capability.assets.length : 0;
                      const canManageCapability = canManageBusinessUnit(
                        partnerProfile,
                        capability.business_unit_id,
                        capability.partner_id,
                      );
                      const deleting = deletingCapabilityId === capability.capability_definition_id;

                      return (
                        <TableRow key={capability.capability_definition_id} hover>
                          <TableCell>
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 260 }}>
                              <TableThumbnail
                                fileId={capability.capability_image_id}
                                alt={capability.capability_name || 'Capability image'}
                              />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                                  {capability.capability_name || 'Unnamed Capability'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                  {capability.source_capability_definition_id || 'No source ID'}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {capability.business_unit_display_name || capability.business_unit_code || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {capability.partner_display_name || capability.partner_code || capability.partner_id || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {capability.category_display_name || capability.category_name || 'Uncategorized'}
                          </TableCell>
                          <TableCell>
                            {optionLabel(FULFILLMENT_MODES, capability.fulfillment_mode)}
                          </TableCell>
                          <TableCell>
                            {optionLabel(ACCEPTANCE_MODES, capability.acceptance_mode)}
                          </TableCell>
                          <TableCell>
                            {optionLabel(CAPABILITY_VISIBILITIES, capability.visibility)}
                          </TableCell>
                          <TableCell>
                            {optionLabel(CAPABILITY_TYPES, capability.capability_type || capability.type)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={optionLabel(CAPABILITY_STATUSES, capability.status)}
                              color={capability.status === 'active' ? 'success' : 'default'}
                              variant={capability.status === 'active' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip size="small" label={assetCount} variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ minWidth: 300 }}>
                              {canManageCapability ? (
                                <>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AddCircleRoundedIcon />}
                                    onClick={() => associateAsset(capability)}
                                  >
                                    Associate Asset
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    startIcon={<DeleteRoundedIcon />}
                                    disabled={deleting}
                                    onClick={() => handleDeleteCapability(capability)}
                                  >
                                    {deleting ? 'Deleting...' : 'Delete'}
                                  </Button>
                                </>
                              ) : null}
                              <Button
                                size="small"
                                variant="contained"
                                endIcon={<ChevronRightRoundedIcon />}
                                onClick={() => openCapability(capability)}
                              >
                                Open
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Stack>
        </Box>
      </Box>

      <Dialog open={Boolean(capabilityFeedback)} onClose={() => setCapabilityFeedback(null)} fullWidth maxWidth="xs">
        <DialogTitle>{capabilityFeedback?.title || 'Capabilities'}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {capabilityFeedback?.message || ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCapabilityFeedback(null)}>
            Back to My Capabilities
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
}
